import type { proto } from '@hiero-ledger/proto';

// A stored signature rule (plTransaction.proposedKey, a company's signatureRequirement) turned into the ledger key the server
// puts on the account, so the browser can check a signatory change before it signs it (#24). It ports the server's path:
// ns-lambda-ts toSignatureKey and replaceIdsWithPublicLedgerPublicKey (_core/public-ledger/public-ledger.ts), then the
// plugin's __transformJsonToPublicLedgerKey (hedera-lambda-plugin hedera.js) and the SDK's KeyList/PublicKey
// _toProtobufKey. Names are dropped and key order is kept; a THRESHOLD_KEY without a numeric threshold is a plain key list,
// as KeyList encodes a null threshold. Pure: the tests check its bytes against the SDK's.

// The DER prefix of an ED25519 public key (SubjectPublicKeyInfo, up to its bit string); the raw key is the 32 bytes after it
const ED25519_DER_PREFIX = '302a300506032b6570032100';
const RAW_HEX = /^[0-9a-f]{64}$/;

const hexBytes = (hex: string) => Uint8Array.from(hex.match(/../g) ?? [], (byte) => parseInt(byte, 16));

/**
 * The raw 32-byte ED25519 key of a public key as nodeStream stores it (the SDK's PublicKey.toString(): DER hex), or of a
 * bare 64-digit hex key, as PublicKey.fromString also reads one. null for anything else.
 */
export function ed25519Raw(publicKey: unknown): Uint8Array | null {
  if (typeof publicKey !== 'string') return null;
  const hex = publicKey.toLowerCase();
  if (hex.startsWith(ED25519_DER_PREFIX) && RAW_HEX.test(hex.slice(ED25519_DER_PREFIX.length))) {
    return hexBytes(hex.slice(ED25519_DER_PREFIX.length));
  }
  return RAW_HEX.test(hex) ? hexBytes(hex) : null;
}

// A plain object's own field, or undefined
const field = (value: unknown, key: string): unknown =>
  typeof value === 'object' && value !== null && Object.prototype.hasOwnProperty.call(value, key)
    ? (value as Record<string, unknown>)[key]
    : undefined;

/**
 * The ledger key a stored rule puts on the account, as a proto Key. Each PERSON_ID becomes that person's public key, from
 * publicKeyOf. Throws on what the server would refuse too: an unknown keyType, a person or key it can't resolve.
 */
export function ledgerKeyFromRule(
  rule: unknown,
  publicKeyOf: (personId: string) => string | null | undefined,
): proto.IKey {
  const keyType = field(rule, 'keyType');
  if (keyType === 'PERSON_ID' || keyType === 'ED25519') {
    const signatoryId = field(rule, 'signatoryId');
    const publicKey =
      keyType === 'PERSON_ID'
        ? publicKeyOf(typeof signatoryId === 'string' ? signatoryId : '')
        : field(rule, 'publicKey');
    const raw = ed25519Raw(publicKey);
    if (!raw) throw new Error(`No ED25519 public key for ${keyType === 'PERSON_ID' ? String(signatoryId) : 'a key'}`);
    return { ed25519: raw };
  }
  if (keyType !== 'KEY_LIST' && keyType !== 'THRESHOLD_KEY') throw new Error(`Unsupported keyType: ${String(keyType)}`);
  const children = field(rule, 'keys');
  const keys = Array.isArray(children) ? children.map((child) => ledgerKeyFromRule(child, publicKeyOf)) : [];
  const threshold = field(rule, 'threshold');
  // KEY_LIST ignores any threshold; a THRESHOLD_KEY with none encodes as a key list
  if (keyType === 'KEY_LIST' || typeof threshold !== 'number') return { keyList: { keys } };
  return { thresholdKey: { threshold, keys: { keys } } };
}
